import { useState, useEffect } from 'react';
import {
  Typography, 
  Card,
  Space,
  Button, 
  Table, 
  Input,
  Tag,
  message,
  Spin,
  Tooltip,
  DatePicker,
  Select,
  Badge,
  Modal,
  InputNumber,
  Row,
  Col,
  Alert,
  Form,
  Descriptions,
  Divider
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  SearchOutlined, 
  EyeOutlined,
  CalendarOutlined,
  FilterOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  SaveOutlined,
  PlusOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { supabase } from '../../../lib/supabase';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { sendWebhook, WEBHOOKS } from '../../../lib/webhooks';
import { formatPrice } from '../../../utils/format';
import { useProductPriceTiers } from '../../../hooks/useProductPriceTiers';
import { calculateExpectedDeliveryDate } from '../../../utils/format';
import AdminNewOrderDialog from '../../../components/AdminNewOrderDialog';

// Enhanced Order Detail component with edit functionality
const OrderDetail = ({ order, onClose, isAdmin, onRefresh, companyName }: { 
  order: any, 
  onClose: () => void, 
  isAdmin?: boolean,
  onRefresh?: () => void,
  companyName?: string
}) => {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editedOrder, setEditedOrder] = useState({ ...order });
  const [orderProducts, setOrderProducts] = useState<any[]>([]);
  const [originalOrderProducts, setOriginalOrderProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [addingProduct, setAddingProduct] = useState(false);
  const [productsToDelete, setProductsToDelete] = useState<string[]>([]);

  // Status options for the order
  const statusOptions = [
    { value: 'nieuw', label: 'Nieuw' },
    { value: 'in_behandeling', label: 'In behandeling' },
    { value: 'verzonden', label: 'Verzonden' },
    { value: 'afgeleverd', label: 'Afgeleverd' },
    { value: 'geannuleerd', label: 'Geannuleerd' }
  ];

  // Use our tiered pricing hook
  const { priceTiers, getPriceForQuantity } = useProductPriceTiers();

  // Fetch order products and available products when edit mode is activated
  useEffect(() => {
    if (order && order.id) {
      fetchOrderProducts();
      
      if (editMode) {
        fetchAvailableProducts();
      }
    }
  }, [order, editMode]);

  const fetchOrderProducts = async () => {
    setProductsLoading(true);
    try {
      // First, fetch the order products
      const { data: orderProductsData, error: orderProductsError } = await supabase
        .from('order_products')
        .select(`
          id,
          hoeveelheid,
          prijs_per_stuk,
          product_id,
          order_id
        `)
        .eq('order_id', order.id);

      if (orderProductsError) throw orderProductsError;
      
      // If there are order products, fetch the product details separately
      if (orderProductsData && orderProductsData.length > 0) {
        // Get all product IDs from the order products
        const productIds = orderProductsData.map(item => item.product_id);
        
        // Fetch the product details for these IDs
        const { data: productsData, error: productsError } = await supabase
          .from('producten')
          .select(`
            id,
            naam,
            artikelnummer,
            prijs
          `)
          .in('id', productIds);
        
        if (productsError) throw productsError;
        
        // Combine the order products with their product details
        const products = orderProductsData.map(orderProduct => {
          const productDetails = productsData?.find(p => p.id === orderProduct.product_id);
          return {
            ...orderProduct,
            product: productDetails
          };
        });
        
        setOrderProducts(products);
        setOriginalOrderProducts(JSON.parse(JSON.stringify(products)));
      } else {
        // No order products found
        setOrderProducts([]);
        setOriginalOrderProducts([]);
      }
    } catch (error) {
      console.error('Error fetching order products:', error);
      message.error('Fout bij het laden van productgegevens');
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchAvailableProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('producten')
        .select('id, naam, artikelnummer, prijs')
        .order('naam');

      if (error) throw error;
      setAvailableProducts(data || []);
    } catch (error) {
      console.error('Error fetching available products:', error);
      message.error('Fout bij het laden van beschikbare producten');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Check if the status is being updated to a different value
      const statusIsChanged = editedOrder.status !== order.status;
      
      console.log('Status change check:', {
        originalStatus: order.status,
        newStatus: editedOrder.status,
        statusIsChanged: statusIsChanged
      });
      
      // First update the order details
      const { error } = await supabase
        .from('orders')
        .update({
          status: editedOrder.status,
          bezorgadres: editedOrder.bezorgadres,
          bezorg_postcode: editedOrder.bezorg_postcode,
          bezorg_stad: editedOrder.bezorg_stad,
          bezorg_land: editedOrder.bezorg_land,
          notities: editedOrder.notities,
                  eigen_referentie: editedOrder.eigen_referentie,
        gewenste_leverdatum: editedOrder.gewenste_leverdatum
        // verwachte_leverdatum removed as we're only using gewenste_leverdatum
        })
        .eq('id', order.id);

      if (error) throw error;
      
      // 1. Handle new products that need to be created in database
      const newProducts = orderProducts.filter(product => product.is_new === true);
      
      if (newProducts.length > 0) {
        const productsToInsert = newProducts.map(p => ({
          order_id: p.order_id,
          product_id: p.product_id,
          hoeveelheid: p.hoeveelheid,
          prijs_per_stuk: p.prijs_per_stuk
        }));
        
        const { data: insertedProducts, error: insertError } = await supabase
          .from('order_products')
          .insert(productsToInsert)
          .select();
        
        if (insertError) throw insertError;
        
        // Update our local state with the real IDs
        if (insertedProducts) {
          // Replace temp products with real ones that have DB-generated IDs
          const updatedProducts = orderProducts.filter(p => !p.is_new);
          insertedProducts.forEach(insertedProduct => {
            const originalProduct = newProducts.find(p => 
              p.product_id === insertedProduct.product_id);
            
            if (originalProduct) {
              updatedProducts.push({
                ...insertedProduct,
                product: originalProduct.product
              });
            }
          });
          
          setOrderProducts(updatedProducts);
        }
      }

      // 2. Process products that need to be deleted
      if (productsToDelete.length > 0) {
        // Only delete products that have real database IDs (not temp ones)
        const realProductIds = productsToDelete.filter(id => !id.startsWith('temp-'));
        
        if (realProductIds.length > 0) {
          const { error: deleteError } = await supabase
            .from('order_products')
            .delete()
            .in('id', realProductIds);
          
          if (deleteError) throw deleteError;
        }
      }

      // 3. Update any changed product quantities for existing products
      const modifiedProducts = orderProducts.filter(current => {
        // Skip new products and deleted products
        if (current.is_new || productsToDelete.includes(current.id)) return false;
        
        const original = originalOrderProducts.find(o => o.id === current.id);
        return original && original.hoeveelheid !== current.hoeveelheid;
      });
      
      // Update quantities in database
      for (const product of modifiedProducts) {
        const { error: updateError } = await supabase
          .from('order_products')
          .update({ hoeveelheid: product.hoeveelheid })
          .eq('id', product.id);
        
        if (updateError) throw updateError;
      }

      // 4. Calculate new total amount based on updated products
      // Only include products that aren't deleted
      const nonDeletedProducts = orderProducts.filter(
        product => !productsToDelete.includes(product.id)
      );
      
      const totalAmount = nonDeletedProducts.reduce(
        (sum, product) => sum + (product.hoeveelheid * product.prijs_per_stuk), 
        0
      );

      // Update the total amount
      const { error: totalUpdateError } = await supabase
        .from('orders')
        .update({ totaal_bedrag: totalAmount })
        .eq('id', order.id);

      if (totalUpdateError) throw totalUpdateError;

      // Reset our tracking states
      setProductsToDelete([]);

      // If the order status changed, trigger the webhook
      if (statusIsChanged) {
        console.log('Status changed, preparing to send webhook...');
        try {
          // Get company information
          const { data: companyData, error: companyError } = await supabase
            .from('companies')
            .select('bedrijfsnaam, email_financieel')
            .eq('user_id', order.klant_id)
            .single();
            
          if (companyError) {
            console.error('Error fetching company data:', companyError);
            throw companyError;
          }
          
          console.log('Company data for webhook:', companyData);
          console.log('Using webhook URL:', WEBHOOKS.ORDER_STATUS_UPDATED);
          
          // Send webhook for status update
          await sendWebhook(WEBHOOKS.ORDER_STATUS_UPDATED, {
            event: 'order.status_updated',
            order_id: order.id,
            ordernummer: order.ordernummer,
            klant_id: order.klant_id,
            bedrijfsnaam: companyData?.bedrijfsnaam || 'Onbekend',
            email_financieel: companyData?.email_financieel,
            previous_status: order.status,
            new_status: editedOrder.status,
            totaal_bedrag: totalAmount,
            bijgewerkt_op: new Date().toISOString()
          });
          
          console.log('Order status update webhook sent successfully');
        } catch (webhookError) {
          console.error('Error sending order status update webhook:', webhookError);
          // Don't throw error here, we still want to save the order update
        }
      }

      message.success('Bestelling succesvol bijgewerkt');
      setEditMode(false);
      
      // Refresh to get the latest data from the server
      if (onRefresh) onRefresh();
      
      // Close the modal
      onClose();
    } catch (error: any) {
      console.error('Error updating order:', error);
      message.error('Fout bij het bijwerken van bestelling: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async () => {
    if (!selectedProduct || productQuantity <= 0) {
      message.error('Selecteer een product en voer een geldig aantal in');
      return;
    }

    setAddingProduct(true);
    try {
      // Find the selected product details
      const productToAdd = availableProducts.find(p => p.id === selectedProduct);
      if (!productToAdd) {
        throw new Error('Geselecteerd product niet gevonden');
      }

      // Apply tiered pricing based on quantity
      const pricePerUnit = getPriceForQuantity(selectedProduct, productQuantity, productToAdd.prijs);

      // Check if the product already exists in the order
      const existingProductIndex = orderProducts.findIndex(p => p.product_id === selectedProduct);
      
      if (existingProductIndex >= 0) {
        // Update existing product quantity (only in local state)
        const updatedProducts = [...orderProducts];
        updatedProducts[existingProductIndex].hoeveelheid += productQuantity;
        
        // Update price based on new total quantity
        const newQuantity = updatedProducts[existingProductIndex].hoeveelheid;
        updatedProducts[existingProductIndex].prijs_per_stuk = 
          getPriceForQuantity(selectedProduct, newQuantity, productToAdd.prijs);
        
        setOrderProducts(updatedProducts);
        message.info('Productaantal bijgewerkt - vergeet niet op te slaan');
      } else {
        // Prepare a new product entry for local state
        // Generate a temporary ID that will be replaced when saving
        const tempId = 'temp-' + Date.now();
        const newProduct = {
          id: tempId,
          order_id: order.id,
          product_id: selectedProduct,
          hoeveelheid: productQuantity,
          prijs_per_stuk: pricePerUnit,
          product: productToAdd,
          // Flag this as a new product that needs to be created on save
          is_new: true
        };
        
        setOrderProducts([...orderProducts, newProduct]);
        message.info('Product toegevoegd - vergeet niet op te slaan');
      }

      // Reset the form
      setSelectedProduct(null);
      setProductQuantity(1);
    } catch (error: any) {
      console.error('Error adding product:', error);
      message.error('Fout bij het toevoegen van product: ' + error.message);
    } finally {
      setAddingProduct(false);
    }
  };

  const handleUpdateQuantity = async (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      message.error('Aantal moet groter zijn dan nul');
      return;
    }

    try {
      const productIndex = orderProducts.findIndex(p => p.id === productId);
      if (productIndex === -1) return;

      // Don't update database immediately, just update local state for better UX
      const updatedProducts = [...orderProducts];
      updatedProducts[productIndex].hoeveelheid = newQuantity;
      
      // Update per-unit price based on new quantity using tiered pricing
      const productInfo = updatedProducts[productIndex];
      if (productInfo.product && productInfo.product.id) {
        updatedProducts[productIndex].prijs_per_stuk = 
          getPriceForQuantity(productInfo.product_id, newQuantity, productInfo.product.prijs);
      }
      
      setOrderProducts(updatedProducts);
      
      // Only show a message if we're making an actual database change
      const hasDifferentQuantity = newQuantity !== orderProducts[productIndex].hoeveelheid;
      if (hasDifferentQuantity) {
        message.info('Aantal bijgewerkt - vergeet niet op te slaan');
      }
    } catch (error: any) {
      console.error('Error updating quantity:', error);
      message.error('Fout bij het bijwerken van aantal: ' + error.message);
    }
  };

  const handleRemoveProduct = async (productId: string) => {
    try {
      // Mark this product for deletion on save, but don't delete from database yet
      setProductsToDelete([...productsToDelete, productId]);
      
      // Remove from local state only
      setOrderProducts(orderProducts.filter(p => p.id !== productId));
      message.info('Product verwijderd - vergeet niet op te slaan');
    } catch (error: any) {
      console.error('Error removing product:', error);
      message.error('Fout bij het verwijderen van product: ' + error.message);
    }
  };

  // Handle cancel by restoring original data
  const handleCancel = () => {
    const hasDeletedProducts = productsToDelete.length > 0;
    
    const hasAddedProducts = orderProducts.some(
      current => !originalOrderProducts.some(original => original.id === current.id)
    );
    
    const hasModifiedProducts = orderProducts.some(current => {
      const original = originalOrderProducts.find(o => o.id === current.id);
      return original && original.hoeveelheid !== current.hoeveelheid;
    });
    
    const hasModifiedOrder = JSON.stringify(editedOrder) !== JSON.stringify(order);
    
    // If there were any changes made
    if (hasDeletedProducts || hasAddedProducts || hasModifiedProducts || hasModifiedOrder) {
      // Ask for confirmation
      Modal.confirm({
        title: 'Wijzigingen ongedaan maken?',
        content: 'Je hebt wijzigingen aangebracht in deze bestelling. Weet je zeker dat je deze wijzigingen wilt ongedaan maken?',
        okText: 'Ja, ongedaan maken',
        cancelText: 'Nee, blijf bewerken',
        onOk: async () => {
          try {
            // Simply restore the UI to original state without DB operations
            // since we haven't actually deleted anything from the database yet
            setOrderProducts([...originalOrderProducts]);
            setEditedOrder({ ...order });
            setProductsToDelete([]); // Clear the delete list
            setEditMode(false);
            message.success('Wijzigingen ongedaan gemaakt');
          } catch (error: any) {
            console.error('Error discarding changes:', error);
            message.error('Fout bij het ongedaan maken van wijzigingen: ' + error.message);
          }
        }
      });
    } else {
      // No changes were made, just exit edit mode
      setEditedOrder({ ...order });
      setProductsToDelete([]); // Clear the delete list just in case
      setEditMode(false);
    }
  };

  // Render the Customer/User information without relying on joined user data
  const renderCustomerInfo = (klantId: string | undefined) => {
    if (!klantId) return '-';
    // Just display the first part of the UUID as a placeholder for now
    return klantId.substring(0, 8) + '...';
  };

  // Get tier information for a product to display in tooltip
  const getProductTierInfo = (productId: string): React.ReactNode => {
    const productTiers = priceTiers.filter(tier => tier.product_id === productId)
      .sort((a, b) => a.min_quantity - b.min_quantity);
    
    if (productTiers.length === 0) return null;
    
    const product = availableProducts.find(p => p.id === productId);
    if (!product) return null;
    
    return (
      <div>
        <p><strong>Staffelprijzen:</strong></p>
        <p>Standaard: {formatPrice(product.prijs)} per stuk</p>
        {productTiers.map(tier => (
          <p key={tier.id}>
            Vanaf {tier.min_quantity} stuks: {formatPrice(tier.price)} per stuk
          </p>
        ))}
      </div>
    );
  };

  const renderEditableOrderProducts = () => {
    if (productsLoading) {
      return <Spin />;
    }

    return (
      <div style={{ marginBottom: 16 }}>
        <Card title="Producten">
          <Table
            dataSource={orderProducts}
            rowKey="id"
            pagination={false}
            columns={[
              {
                title: 'Product',
                key: 'product',
                render: (_, record) => record.product?.naam || 'Onbekend'
              },
              {
                title: 'Artikelnummer',
                key: 'artikelnummer',
                render: (_, record) => record.product?.artikelnummer || 'N/A'
              },
              {
                title: 'Aantal',
                key: 'aantal',
                render: (_, record) => (
                  <InputNumber
                    min={1}
                    value={record.hoeveelheid}
                    onChange={(value) => value && handleUpdateQuantity(record.id, value)}
                    style={{ width: 80 }}
                    step={0.1}
                    stringMode
                  />
                )
              },
              {
                title: 'Prijs per stuk',
                key: 'prijs',
                render: (_, record) => {
                  const hasTierPricing = priceTiers.some(tier => tier.product_id === record.product_id);
                  const basePrice = record.product?.prijs || 0;
                  const actualPrice = record.prijs_per_stuk || basePrice;
                  const hasDiscount = actualPrice < basePrice;
                  
                  return (
                    <div>
                      {formatPrice(actualPrice)}
                      {hasDiscount && <Tag color="green" style={{ marginLeft: 8 }}>Staffelkorting</Tag>}
                      {hasTierPricing && (
                        <Tooltip title={getProductTierInfo(record.product_id)}>
                          <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                        </Tooltip>
                      )}
                    </div>
                  );
                }
              },
              {
                title: 'Totaal',
                key: 'totaal',
                render: (_, record) => formatPrice(record.hoeveelheid * record.prijs_per_stuk)
              },
                              {
                  title: 'Gewenste leverdatum',
                  key: 'delivery_date',
                  render: (_, record) => calculateExpectedDeliveryDate(order.aangemaakt_op, record.product?.levertijd_dagen)
              },
              {
                title: 'Actie',
                key: 'actie',
                render: (_, record) => (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveProduct(record.id)}
                  />
                )
              }
            ]}
            summary={() => {
              const total = orderProducts.reduce(
                (sum, product) => sum + (product.hoeveelheid * product.prijs_per_stuk),
                0
              );
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}><strong>Totaal</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}><strong>{formatPrice(total)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
              );
            }}
          />

          <div style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col span={10}>
                <Select
                  placeholder="Selecteer een product"
                  value={selectedProduct}
                  onChange={setSelectedProduct}
                  style={{ width: '100%' }}
                >
                  {availableProducts.map(product => (
                    <Select.Option key={product.id} value={product.id}>
                      {product.naam} ({product.artikelnummer}) - {formatPrice(product.prijs)}
                      {priceTiers.some(tier => tier.product_id === product.id) && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>Staffelprijzen</Tag>
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <InputNumber
                  placeholder="Aantal"
                  min={1}
                  value={productQuantity}
                  onChange={(value) => value && setProductQuantity(value)}
                  style={{ width: '100%' }}
                  step={0.1}
                  stringMode
                />
              </Col>
              <Col span={8}>
                <Button
                  type="primary"
                  onClick={handleAddProduct}
                  loading={addingProduct}
                  disabled={!selectedProduct || productQuantity <= 0}
                >
                  Product toevoegen
                </Button>
              </Col>
            </Row>
            {selectedProduct && priceTiers.some(tier => tier.product_id === selectedProduct) && (
              <Alert
                message="Product heeft staffelprijzen"
                description={getProductTierInfo(selectedProduct)}
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        </Card>
      </div>
    );
  };

  return (
    <Modal
      title={`Bestelling ${order.ordernummer || '#' + order.id.substring(0, 6)}`}
      open={true}
      onCancel={editMode ? handleCancel : onClose}
      width={800}
      footer={[
        editMode ? (
          <Button key="cancel" onClick={handleCancel}>
            Annuleren
          </Button>
        ) : (
          <Button key="close" onClick={onClose}>
            Sluiten
          </Button>
        ),
        isAdmin && !editMode && (
          <Button 
            key="edit" 
            type="primary"
            icon={<EditOutlined />} 
            onClick={() => setEditMode(true)}
          >
            Bewerken
          </Button>
        ),
        editMode && (
          <Button 
            key="save" 
            type="primary" 
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={loading}
          >
            Opslaan
          </Button>
        )
      ]}
    >
      <div>
        {editMode ? (
          // Editable order form
          <div>
            <Form
              layout="vertical"
              initialValues={editedOrder}
              onValuesChange={(_, values) => setEditedOrder({ ...editedOrder, ...values })}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Status" name="status">
                    <Select options={statusOptions} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Ordernummer">
                    <Input value={order.ordernummer || '-'} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Eigen referentie" name="eigen_referentie">
                    <Input placeholder="Voer een eigen referentie in" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Gewenste leverdatum" name="gewenste_leverdatum">
                    <Input type="date" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">Bezorggegevens</Divider>
              
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Adres" name="bezorgadres">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Postcode" name="bezorg_postcode">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Stad" name="bezorg_stad">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Land" name="bezorg_land">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              
              <Divider orientation="left">Notities</Divider>
              
              <Form.Item name="notities">
                <Input.TextArea rows={4} />
              </Form.Item>
            </Form>

            {renderEditableOrderProducts()}
          </div>
        ) : (
          // View-only order details
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Ordernummer">
                {order.ordernummer || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge 
                  status={
                    order.status === 'nieuw' ? 'processing' : 
                    order.status === 'in_behandeling' ? 'warning' :
                    order.status === 'verzonden' ? 'success' :
                    order.status === 'afgeleverd' ? 'success' :
                    'error'
                  } 
                  text={
                    order.status === 'nieuw' ? 'Nieuw' : 
                    order.status === 'in_behandeling' ? 'In behandeling' :
                    order.status === 'verzonden' ? 'Verzonden' :
                    order.status === 'afgeleverd' ? 'Afgeleverd' :
                    'Geannuleerd'
                  } 
                />
              </Descriptions.Item>
              <Descriptions.Item label="Datum">
                {dayjs(order.aangemaakt_op).format('DD-MM-YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Bedrag">
                {formatPrice(order.totaal_bedrag)}
              </Descriptions.Item>
              <Descriptions.Item label="Klant" span={2}>
                {companyName || renderCustomerInfo(order.klant_id)}
              </Descriptions.Item>
              <Descriptions.Item label="Eigen referentie">
                {order.eigen_referentie || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Gewenste leverdatum">
                {order.gewenste_leverdatum 
                  ? new Date(order.gewenste_leverdatum).toLocaleDateString('nl-NL') 
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Bezorgadres" span={2}>
                {order.bezorgadres}, {order.bezorg_postcode} {order.bezorg_stad}, {order.bezorg_land}
              </Descriptions.Item>
              {order.notities && (
                <Descriptions.Item label="Notities" span={2}>
                  {order.notities}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left">Producten</Divider>
            
            {productsLoading ? (
              <Spin />
            ) : (
              <Table
                dataSource={orderProducts}
                rowKey="id"
                pagination={false}
                columns={[
                  {
                    title: 'Product',
                    key: 'product',
                    render: (_, record) => record.product?.naam || 'Onbekend'
                  },
                  {
                    title: 'Artikelnummer',
                    key: 'artikelnummer',
                    render: (_, record) => record.product?.artikelnummer || 'N/A'
                  },
                  {
                    title: 'Aantal',
                    dataIndex: 'hoeveelheid',
                    key: 'aantal',
                  },
                  {
                    title: 'Prijs per stuk',
                    key: 'prijs',
                    render: (_, record) => {
                      const hasTierPricing = priceTiers.some(tier => tier.product_id === record.product_id);
                      const basePrice = record.product?.prijs || 0;
                      const actualPrice = record.prijs_per_stuk || basePrice;
                      const hasDiscount = actualPrice < basePrice;
                      
                      return (
                        <div>
                          {formatPrice(actualPrice)}
                          {hasDiscount && <Tag color="green" style={{ marginLeft: 8 }}>Staffelkorting</Tag>}
                          {hasTierPricing && (
                            <Tooltip title={getProductTierInfo(record.product_id)}>
                              <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                            </Tooltip>
                          )}
                        </div>
                      );
                    }
                  },
                  {
                    title: 'Totaal',
                    key: 'totaal',
                    render: (_, record) => formatPrice(record.hoeveelheid * record.prijs_per_stuk)
                  },
                  {
                    title: 'Gewenste leverdatum',
                    key: 'delivery_date',
                    render: () => order.gewenste_leverdatum ? new Date(order.gewenste_leverdatum).toLocaleDateString('nl-NL') : '-'
                  }
                ]}
                summary={() => {
                  const total = orderProducts.reduce(
                    (sum, product) => sum + (product.hoeveelheid * product.prijs_per_stuk),
                    0
                  );
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={4}><strong>Totaal</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}><strong>{formatPrice(total)}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface Order {
  id: string;
  klant_id: string;
  ordernummer?: string;
  status: 'nieuw' | 'in_behandeling' | 'verzonden' | 'afgeleverd' | 'geannuleerd';
  totaal_bedrag: number;
  bezorgadres: string;
  bezorg_postcode: string;
  bezorg_stad: string;
  bezorg_land: string;
  notities?: string;
  // verwachte_leverdatum removed as we're only using gewenste_leverdatum
  gewenste_leverdatum?: string;
  aangemaakt_op: string;
  bijgewerkt_op: string;
  user?: {
    email: string;
    id: string;
  }
}

const statusColors = {
  nieuw: 'blue',
  in_behandeling: 'orange',
  verzonden: 'purple',
  afgeleverd: 'green',
  geannuleerd: 'red',
};

const statusLabels = {
  nieuw: 'Nieuw',
  in_behandeling: 'In behandeling',
  verzonden: 'Verzonden',
  afgeleverd: 'Afgeleverd',
  geannuleerd: 'Geannuleerd',
};

interface OrdersAdminProps {
  viewSingle?: boolean;
}

export default function OrdersAdmin({ viewSingle = false }: OrdersAdminProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
  const [isNewOrderModalVisible, setIsNewOrderModalVisible] = useState(false);

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (viewSingle && id) {
      fetchSingleOrder(id);
    } else {
      fetchOrders();
    }
  }, [viewSingle, id, dateRange, statusFilter]);

  // Fetch company names when orders change
  useEffect(() => {
    if (orders.length > 0) {
      fetchCompanyNames();
    }
  }, [orders]);

  async function fetchCompanyNames() {
    try {
      // Get unique klant_ids
      const userIds = Array.from(new Set(orders.map(order => order.klant_id))).filter(Boolean);
      
      if (userIds.length === 0) return;
      
      console.log('Fetching company names for user IDs:', userIds);
      
      const { data, error } = await supabase
        .from('companies')
        .select('user_id, bedrijfsnaam')
        .in('user_id', userIds);
        
      if (error) {
        console.error('Fout bij het ophalen van bedrijfsnamen:', error);
        return;
      }
      
      console.log('Company data retrieved:', data);
      
      // Create a mapping of user_id to bedrijfsnaam
      const companyMap: Record<string, string> = {};
      data?.forEach(company => {
        if (company.user_id && company.bedrijfsnaam) {
          companyMap[company.user_id] = company.bedrijfsnaam;
        }
      });
      
      console.log('Final company mapping:', companyMap);
      
      setCompanyNames(companyMap);
    } catch (error) {
      console.error('Error fetching company names:', error);
    }
  }

  async function fetchOrders() {
    setLoading(true);
    try {
      console.log('Bestellingen ophalen...');
      
      // Simple query without trying to join with companies table
      let query = supabase
        .from('orders')
        .select('*')
        .order('aangemaakt_op', { ascending: false });
      
      if (dateRange && dateRange[0] && dateRange[1]) {
        query = query
          .gte('aangemaakt_op', dateRange[0].startOf('day').toISOString())
          .lte('aangemaakt_op', dateRange[1].endOf('day').toISOString());
      }
      
      if (statusFilter.length > 0) {
        query = query.in('status', statusFilter);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Fout bij het ophalen van bestellingen:', error);
        throw error;
      }
      
      console.log(`${data?.length || 0} bestellingen succesvol opgehaald`);
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Fout bij het ophalen van bestellingen');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSingleOrder(orderId: string) {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .single();

      if (error) throw error;
      if (data) {
        setCurrentOrder(data);
        setIsDetailVisible(true);
      }
    } catch (error: any) {
      console.error('Error fetching order:', error);
      message.error('Fout bij het laden van bestelling: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  // Apply filters when they change
  useEffect(() => {
    fetchOrders();
  }, [dateRange, statusFilter]);

  const filteredOrders = orders.filter(order => {
    const searchableText = [
      order.id,
      order.ordernummer || '',
      order.klant_id || '',
      companyNames[order.klant_id] || '',
      order.bezorgadres,
      order.bezorg_stad,
      order.bezorg_postcode,
    ].join(' ').toLowerCase();
    
    return searchableText.includes(searchQuery.toLowerCase());
  });

  const handleRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(null);
    }
  };

  const handleStatusChange = (values: string[]) => {
    setStatusFilter(values);
  };

  const resetFilters = () => {
    setDateRange(null);
    setStatusFilter([]);
    setSearchQuery('');
  };

  const handleViewOrder = (order: Order) => {
    setCurrentOrder(order);
    setIsDetailVisible(true);
  };

  const handleEditOrder = (order: Order) => {
    // Since there's no separate edit page, show the order details in a modal with edit functionality
    setCurrentOrder(order);
    setIsDetailVisible(true);
    // We'll enhance the OrderDetail component later to include editing capabilities
  };

  const handleDeleteOrder = async (order: Order) => {
    Modal.confirm({
      title: 'Weet je zeker dat je deze bestelling wilt verwijderen?',
      content: 'Deze actie kan niet ongedaan worden gemaakt.',
      okText: 'Ja, Verwijderen',
      okType: 'danger',
      cancelText: 'Annuleren',
      onOk: async () => {
        try {
          // First delete related order products (not order_items - this was incorrect)
          const { error: itemsError } = await supabase
            .from('order_products')
            .delete()
            .eq('order_id', order.id);
            
          if (itemsError) throw itemsError;
          
          // Then delete the order
          const { error } = await supabase
            .from('orders')
            .delete()
            .eq('id', order.id);
            
          if (error) throw error;
          
          message.success('Bestelling succesvol verwijderd');
          fetchOrders(); // Refresh the list
        } catch (error: any) {
          console.error('Error deleting order:', error);
          message.error('Fout bij het verwijderen van bestelling: ' + error.message);
        }
      },
    });
  };

  const handleCreateOrder = async (orderData: any) => {
    try {
      setLoading(true);
      
      console.log('Creating new order:', orderData);
      
      // First create the order
      const { data: orderData2, error: orderError } = await supabase
        .from('orders')
        .insert({
          klant_id: orderData.klant_id,
          status: 'nieuw',
          bezorgadres: orderData.bezorgadres,
          bezorg_postcode: orderData.bezorg_postcode,
          bezorg_stad: orderData.bezorg_stad,
          bezorg_land: orderData.bezorg_land || 'Nederland',
          notities: orderData.notities,
          eigen_referentie: orderData.eigen_referentie,
          gewenste_leverdatum: orderData.gewenste_leverdatum,
          // Set a default total amount of 0, we'll update this after adding products
          totaal_bedrag: 0
        })
        .select()
        .single();
      
      if (orderError) throw orderError;
      
      if (!orderData2) {
        throw new Error('Fout bij het aanmaken van order - geen order ID geretourneerd');
      }
      
      const newOrderId = orderData2.id;
      
      console.log('Order created with ID:', newOrderId);
      
      // Now add the products to the order
      const orderProducts = orderData.products.map((product: any) => ({
        order_id: newOrderId,
        product_id: product.product_id,
        hoeveelheid: product.hoeveelheid,
        // Get the price per unit for this product and quantity
        prijs_per_stuk: 0 // We'll calculate this in a second pass
      }));
      
      // Insert order products
      const { error: productsError } = await supabase
        .from('order_products')
        .insert(orderProducts);
      
      if (productsError) throw productsError;
      
      // Now fetch the products to calculate proper pricing with tiers
      const { data: productsData, error: productsDataError } = await supabase
        .from('producten')
        .select('id, prijs');
      
      if (productsDataError) throw productsDataError;
      
      // Fetch price tiers
      const { data: tiersData, error: tiersError } = await supabase
        .from('product_price_tiers')
        .select('*');
      
      if (tiersError) throw tiersError;
      
      // Create a function to calculate the price for a given product and quantity
      const calculatePriceWithTiers = (productId: string, quantity: number) => {
        const product = productsData?.find(p => p.id === productId);
        if (!product) return 0;
        
        const basePrice = product.prijs;
        
        // Find applicable tier
        const productTiers = (tiersData || [])
          .filter(tier => tier.product_id === productId)
          .sort((a, b) => b.min_quantity - a.min_quantity); // Sort by min_quantity descending
        
        // Find the highest tier that applies to this quantity
        const applicableTier = productTiers.find(tier => quantity >= tier.min_quantity);
        
        return applicableTier ? applicableTier.price : basePrice;
      };
      
      // Fetch the created order products
      const { data: createdOrderProducts, error: fetchError } = await supabase
        .from('order_products')
        .select('id, product_id, hoeveelheid')
        .eq('order_id', newOrderId);
      
      if (fetchError) throw fetchError;
      
      // Update each product with the correct price
      for (const product of createdOrderProducts || []) {
        const pricePerUnit = calculatePriceWithTiers(product.product_id, product.hoeveelheid);
        
        await supabase
          .from('order_products')
          .update({ prijs_per_stuk: pricePerUnit })
          .eq('id', product.id);
      }
      
      // Calculate the total order amount
      let totalAmount = 0;
      for (const product of createdOrderProducts || []) {
        const pricePerUnit = calculatePriceWithTiers(product.product_id, product.hoeveelheid);
        totalAmount += pricePerUnit * product.hoeveelheid;
      }
      
      // Update the order with the calculated total
      const { error: updateTotalError } = await supabase
        .from('orders')
        .update({ totaal_bedrag: totalAmount })
        .eq('id', newOrderId);
      
      if (updateTotalError) throw updateTotalError;

      // Generate a proper client order number consistent with the client orders
      const generateClientOrderNumber = () => {
        const now = new Date();
        const shortYear = now.getFullYear().toString().slice(-2);
        
        const cleanUserId = orderData.klant_id.replace(/-/g, '');
        const companyCode = cleanUserId.slice(0, 6).toUpperCase();
        
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const seconds = now.getSeconds().toString().padStart(2, '0');
        const ms = now.getMilliseconds().toString().padStart(3, '0');
        
        const timeSequence = parseInt(`${hours}${minutes}${seconds}${ms}`);
        const seqNumber = (timeSequence % 9999) + 1;
        const sequentialNumber = seqNumber.toString().padStart(4, '0');
        
        return `ORD${shortYear}-${companyCode}-${sequentialNumber}`;
      };

      const orderNumber = generateClientOrderNumber();
      
      const { error: orderNumberError } = await supabase
        .from('orders')
        .update({ ordernummer: orderNumber })
        .eq('id', newOrderId);
      
      if (orderNumberError) throw orderNumberError;

      // Now handle the webhook notifications exactly like in client order process
      try {
        // Get the updated order with the order number
        const { data: order, error: orderRefreshError } = await supabase
          .from('orders')
          .select('*')
          .eq('id', newOrderId)
          .single();
        
        if (orderRefreshError) throw orderRefreshError;
        
        // Fetch product details for the PDF
        const { data: productDetails } = await supabase
          .from('order_products')
          .select(`
            id,
            order_id,
            product_id,
            hoeveelheid,
            prijs_per_stuk,
            product:producten!product_id (
              id,
              naam,
              beschrijving,
              artikelnummer,
              prijs,
              aangemaakt_op,
              bijgewerkt_op,
              levertijd_dagen,
              leverancier_id,
              palletisering
            )
          `)
          .eq('order_id', order.id);
        
        if (productDetails && productDetails.length > 0) {
          console.log('Generating order confirmation PDF...');
          
          try {
            // Define company info for the PDF
            const companyInfo = {
              name: 'RE-PACKED',
              address: 'Zure Vischsteeg 12',
              postalCode: '3143 BB',
              city: 'Maassluis',
              country: 'Nederland',
              contactEmail: 'info@re-packed.nl',
              contactPhone: '+31612962202',
            };
            
            // Import and use the proper PDF generator function
            const { generateOrderConfirmationPDF, generateAndSaveSupplierPurchaseOrders } = await import('../../../lib/pdfGenerator');
            
            // Generate a complete PDF with all order details and products
            const pdfBlob = await generateOrderConfirmationPDF(
              order, 
              productDetails as any,
              companyInfo
            );
            
            // Store PDF in Supabase Storage
            const pdfFileName = `orderbevestiging-${order.ordernummer || order.id.slice(0, 8)}.pdf`;
            const { data: storageData, error: storageError } = await supabase
              .storage
              .from('order-pdfs') // Make sure this bucket exists
              .upload(`orders/${pdfFileName}`, pdfBlob, {
                contentType: 'application/pdf',
                upsert: true
              });
              
            if (storageError) {
              console.error('Error storing PDF in Supabase:', storageError);
            } else {
              console.log('PDF stored in Supabase Storage:', storageData);
              
              // Add PDF URL reference to the order
              const { error: updateError } = await supabase
                .from('orders')
                .update({ 
                  pdf_url: `orders/${pdfFileName}` 
                })
                .eq('id', order.id);
                
              if (updateError) {
                console.error('Error updating order with PDF URL:', updateError);
              }
            }
            
            // Convert order confirmation PDF to base64
            const pdfBase64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64data = reader.result as string;
                // Remove the data URL prefix
                const base64Content = base64data.split(',')[1];
                resolve(base64Content);
              };
              reader.readAsDataURL(pdfBlob);
            });
            
            // Also fetch the logo and convert to base64 for the webhook
            let logoBase64 = null;
            try {
              const logoUrl = 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png';
              const logoResponse = await fetch(logoUrl);
              const logoBlob = await logoResponse.blob();
              
              logoBase64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64data = reader.result as string;
                  // Remove the data URL prefix
                  const base64Content = base64data.split(',')[1];
                  resolve(base64Content);
                };
                reader.readAsDataURL(logoBlob);
              });
              
              console.log('Logo converted to base64 successfully');
            } catch (logoError) {
              console.error('Error converting logo to base64:', logoError);
            }
            
            // Fetch customer company information
            let customerCompany: {
              name: string;
              contact_person?: string;
              email?: string;
              phone?: string;
            } | undefined;
            
            try {
              const { data: companyData, error: companyError } = await supabase
                .from('companies')
                .select('bedrijfsnaam, voornaam_financieel, achternaam_financieel, telefoon_financieel, email_financieel')
                .eq('user_id', order.klant_id)
                .single();
              
              if (companyData && !companyError) {
                customerCompany = {
                  name: companyData.bedrijfsnaam,
                  contact_person: companyData.voornaam_financieel && companyData.achternaam_financieel 
                    ? `${companyData.voornaam_financieel} ${companyData.achternaam_financieel}` 
                    : undefined,
                  email: companyData.email_financieel,
                  phone: companyData.telefoon_financieel
                };
              }
            } catch (error) {
              console.error('Error fetching customer company data:', error);
            }
            
            // Generate supplier purchase order PDFs
            // 1. Get unique supplier IDs from the order products
            const uniqueSupplierIds = Array.from(new Set(
              productDetails
                .map((p: any) => p.product?.leverancier_id)
                .filter((id: any) => id)
            )) as string[];
            
            // 2. Fetch supplier information
            if (uniqueSupplierIds.length > 0) {
              const { data: supplierData, error: supplierError } = await supabase
                .from('leveranciers')
                .select('id, naam, contactpersoon, email, telefoon')
                .in('id', uniqueSupplierIds);
              
              if (supplierError) {
                console.error('Error fetching supplier data:', supplierError);
              } else if (supplierData && supplierData.length > 0) {
                // Transform supplier data into the required format
                const suppliersMap: {
                  [key: string]: {
                    id: string;
                    name: string;
                    contactPerson: string;
                    email?: string;
                    phone?: string;
                  }
                } = {};
                
                supplierData.forEach(supplier => {
                  suppliersMap[supplier.id] = {
                    id: supplier.id,
                    name: supplier.naam,
                    contactPerson: supplier.contactpersoon,
                    email: supplier.email,
                    phone: supplier.telefoon
                  };
                });
                
                // 3. Generate and save supplier purchase order PDFs
                try {
                  const supplierPDFs = await generateAndSaveSupplierPurchaseOrders(
                    order,
                    productDetails as any,
                    suppliersMap,
                    customerCompany
                  );
                  
                  console.log(`Generated ${supplierPDFs.length} supplier purchase order PDFs`);
                  
                  // 4. Add supplier PDF URLs to the webhook data
                  if (supplierPDFs.length > 0) {
                    // We'll make an array of supplier PDF information to include in the webhook
                    const supplierPDFsInfo = await Promise.all(
                      supplierPDFs.map(async pdf => {
                        // Get the PDF file from storage to convert to base64
                        const fileName = pdf.storageUrl.split('/').pop() || '';
                        
                        try {
                          // Get public URL and download the file
                          const response = await fetch(pdf.storageUrl);
                          const pdfBlob = await response.blob();
                          
                          // Convert PDF to base64
                          const pdfBase64 = await new Promise<string>((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              const base64data = reader.result as string;
                              // Remove the data URL prefix
                              const base64Content = base64data.split(',')[1];
                              resolve(base64Content);
                            };
                            reader.readAsDataURL(pdfBlob);
                          });
                          
                          return {
                            supplier_id: pdf.supplierId,
                            supplier_name: pdf.supplierName,
                            pdf_url: pdf.storageUrl,
                            pdf_filename: fileName,
                            pdf_base64: pdfBase64
                          };
                        } catch (error) {
                          console.error(`Error processing supplier PDF ${fileName}:`, error);
                          return {
                            supplier_id: pdf.supplierId,
                            supplier_name: pdf.supplierName,
                            pdf_url: pdf.storageUrl,
                            pdf_filename: fileName
                          };
                        }
                      })
                    );
                    
                    console.log(`Processed ${supplierPDFsInfo.length} supplier PDFs for webhook`);
                    
                    // Store this for use in the webhook call
                    const supplierPdfDetails = {
                      supplier_pdfs: supplierPDFsInfo,
                      has_supplier_pdfs: true,
                      supplier_count: supplierPDFsInfo.length
                    };
                    
                    // Send to webhook with all order data including supplier PDFs
                    await sendWebhook(WEBHOOKS.ORDER_CREATED, {
                      event: 'order.created',
                      order_id: order.id,
                      ordernummer: order.ordernummer,
                      klant_id: order.klant_id,
                      status: order.status,
                      totaal_bedrag: order.totaal_bedrag,
                      eigen_referentie: order.eigen_referentie,
                      aangemaakt_op: order.aangemaakt_op,
                      producten: orderData.products.length,
                      pdf_base64: pdfBase64,
                      pdf_filename: `orderbevestiging-${order.ordernummer || order.id.slice(0, 8)}.pdf`,
                      logo_url: 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png',
                      logo_base64: logoBase64,
                      // Include customer company information
                      customer_company: customerCompany,
                      // Include supplier PDF information with base64 content
                      ...supplierPdfDetails
                    });
                    
                    console.log('Order confirmation with supplier PDFs sent to webhook');
                    return; // We've already sent the webhook
                  }
                } catch (supplierPdfError) {
                  console.error('Error generating supplier purchase order PDFs:', supplierPdfError);
                }
              }
            }
            
            // Send order confirmation webhook (fallback if no supplier PDFs)
            await sendWebhook(WEBHOOKS.ORDER_CREATED, {
              event: 'order.created',
              order_id: order.id,
              ordernummer: order.ordernummer,
              klant_id: order.klant_id,
              status: order.status,
              totaal_bedrag: order.totaal_bedrag,
              eigen_referentie: order.eigen_referentie,
              aangemaakt_op: order.aangemaakt_op,
              producten: orderData.products.length,
              pdf_base64: pdfBase64,
              pdf_filename: `orderbevestiging-${order.ordernummer || order.id.slice(0, 8)}.pdf`,
              logo_url: 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png',
              logo_base64: logoBase64,
              // Include customer company information
              customer_company: customerCompany,
              // Include supplier PDF information - we include URLs rather than base64 to avoid huge payloads
              has_supplier_pdfs: uniqueSupplierIds.length > 0,
              supplier_count: uniqueSupplierIds.length
            });
            
            console.log('Order confirmation webhook sent successfully');
          } catch (pdfError) {
            console.error('Error generating PDF:', pdfError);
            
            // If PDF generation fails, still send the webhook without the PDF
            let logoBase64 = null;
            try {
              const logoUrl = 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png';
              const logoResponse = await fetch(logoUrl);
              const logoBlob = await logoResponse.blob();
              
              logoBase64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64data = reader.result as string;
                  // Remove the data URL prefix
                  const base64Content = base64data.split(',')[1];
                  resolve(base64Content);
                };
                reader.readAsDataURL(logoBlob);
              });
            } catch (logoError) {
              console.error('Error converting logo to base64:', logoError);
            }
            
            await sendWebhook(WEBHOOKS.ORDER_CREATED, {
              event: 'order.created',
              order_id: order.id,
              ordernummer: order.ordernummer,
              klant_id: order.klant_id,
              status: order.status,
              totaal_bedrag: order.totaal_bedrag,
              eigen_referentie: order.eigen_referentie,
              aangemaakt_op: order.aangemaakt_op,
              producten: orderData.products.length,
              logo_url: 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png',
              logo_base64: logoBase64,
              // Include customer company information
              customer_company: customerCompany
            });
          }
        } else {
          // No product details found, send webhook without PDFs
          // Fetch customer company information
          let customerCompany: {
            name: string;
            contact_person?: string;
            email?: string;
            phone?: string;
          } | undefined;
          
          try {
            const { data: companyData, error: companyError } = await supabase
              .from('companies')
              .select('bedrijfsnaam, voornaam, achternaam, telefoon, email')
              .eq('user_id', order.klant_id)
              .single();
            
            if (companyData && !companyError) {
              customerCompany = {
                name: companyData.bedrijfsnaam,
                contact_person: companyData.voornaam && companyData.achternaam 
                  ? `${companyData.voornaam} ${companyData.achternaam}` 
                  : undefined,
                email: companyData.email,
                phone: companyData.telefoon
              };
            }
          } catch (error) {
            console.error('Error fetching customer company data:', error);
          }
          
          let logoBase64 = null;
          try {
            const logoUrl = 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png';
            const logoResponse = await fetch(logoUrl);
            const logoBlob = await logoResponse.blob();
            
            logoBase64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64data = reader.result as string;
                // Remove the data URL prefix
                const base64Content = base64data.split(',')[1];
                resolve(base64Content);
              };
              reader.readAsDataURL(logoBlob);
            });
          } catch (logoError) {
            console.error('Error converting logo to base64:', logoError);
          }
          
          await sendWebhook(WEBHOOKS.ORDER_CREATED, {
            event: 'order.created',
            order_id: order.id,
            ordernummer: order.ordernummer,
            klant_id: order.klant_id,
            status: order.status,
            totaal_bedrag: order.totaal_bedrag,
            eigen_referentie: order.eigen_referentie,
            aangemaakt_op: order.aangemaakt_op,
            producten: orderData.products.length,
            logo_url: 'https://ivtllicbozkouaeurjjc.supabase.co/storage/v1/object/public/logos/repacked-logo.png',
            logo_base64: logoBase64,
            // Include customer company information
            customer_company: customerCompany
          });
        }
      } catch (webhookError) {
        console.error('Error sending order webhooks:', webhookError);
        // Don't fail the order creation if webhooks fail
      }

      message.success('Order succesvol aangemaakt');
      setIsNewOrderModalVisible(false);
      fetchOrders();
    } catch (error: any) {
      console.error('Error creating order:', error);
      message.error(`Fout bij het aanmaken van order: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<Order> = [
    {
      title: 'Ordernummer',
      key: 'ordernummer',
      width: '15%',
      ellipsis: true,
      render: (_, record) => {
        const displayValue = record.ordernummer || record.id.substring(0, 8);
        return (
          <Tooltip title={record.id}>
            <span>{displayValue}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Eigen referentie',
      dataIndex: 'eigen_referentie',
      key: 'eigen_referentie',
      width: '15%',
      ellipsis: true,
      render: (eigen_referentie) => eigen_referentie || '-',
    },
    {
      title: 'Bedrijf',
      key: 'customer',
      width: '20%',
      ellipsis: true,
      responsive: ['md'],
      render: (_, record) => {
        // Display company name if available, otherwise fallback to user ID
        return companyNames[record.klant_id] || 
          (record.klant_id ? record.klant_id.substring(0, 8) + '...' : '-');
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (status: keyof typeof statusColors) => (
        <Tag color={statusColors[status] || 'default'}>
          {statusLabels[status] || status}
        </Tag>
      ),
      filters: Object.keys(statusLabels).map(key => ({
        text: statusLabels[key as keyof typeof statusLabels],
        value: key,
      })),
      filteredValue: statusFilter,
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Bedrag',
      dataIndex: 'totaal_bedrag',
      key: 'totaal_bedrag',
      width: '15%',
      render: (amount: number) => 
        new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount),
      sorter: (a, b) => a.totaal_bedrag - b.totaal_bedrag,
      responsive: ['sm'],
    },
    {
      title: 'Datum',
      dataIndex: 'aangemaakt_op',
      key: 'aangemaakt_op',
      width: '15%',
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
      sorter: (a, b) => 
        dayjs(a.aangemaakt_op).unix() - dayjs(b.aangemaakt_op).unix(),
    },
    {
      title: 'Acties',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Space>
          <Button 
            type="link"
            icon={<EyeOutlined />} 
            onClick={() => handleViewOrder(record)}
            size={isMobile ? 'small' : 'middle'}
            title="Bekijk bestelling"
          />
          <Button 
            type="link"
            icon={<EditOutlined />} 
            onClick={() => handleEditOrder(record)}
            size={isMobile ? 'small' : 'middle'}
            title="Bewerk bestelling"
          />
          <Button 
            type="link"
            icon={<DeleteOutlined />} 
            danger
            onClick={() => handleDeleteOrder(record)}
            size={isMobile ? 'small' : 'middle'}
            title="Verwijder bestelling"
          />
        </Space>
      ),
    },
  ];

  // If viewing a single order, just show the order detail
  if (viewSingle && id) {
    return (
      <div>
        {isDetailVisible && currentOrder ? (
          <OrderDetail 
            order={currentOrder} 
            onClose={() => navigate('/admin/orders')}
            isAdmin={true}
            onRefresh={fetchOrders}
            companyName={currentOrder.klant_id ? companyNames[currentOrder.klant_id] : undefined}
          />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <Typography.Text type="secondary">Bestelling laden...</Typography.Text>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        style={{
          borderRadius: '8px',
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '16px' : '0'
          }}>
            <Title level={4} style={{ margin: 0 }}>Bestellingen Beheer</Title>
            <Space 
              size="middle" 
              wrap={true}
              style={{ 
                width: isMobile ? '100%' : 'auto',
                display: 'flex',
                flexWrap: 'wrap'
              }}
            >
              <Input
                placeholder="Zoek bestellingen"
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ 
                  width: isMobile ? '100%' : 200 
                }}
              />
              
              <RangePicker
                value={dateRange}
                onChange={handleRangeChange}
                placeholder={['Startdatum', 'Einddatum']}
                prefix={<CalendarOutlined />}
                style={{ 
                  width: isMobile ? '100%' : 'auto' 
                }}
              />
              
              <Select
                mode="multiple"
                placeholder="Filter op status"
                value={statusFilter}
                onChange={handleStatusChange}
                style={{ minWidth: 180, width: isMobile ? '100%' : 'auto' }}
                allowClear
              >
                {Object.entries(statusLabels).map(([value, label]) => (
                  <Option key={value} value={value}>
                    <Badge color={statusColors[value as keyof typeof statusColors]} />
                    {label}
                  </Option>
                ))}
              </Select>
              
              <Button 
                type="link"
                onClick={resetFilters}
                icon={<FilterOutlined />}
              >
                Filters Wissen
              </Button>
              
              <Button 
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsNewOrderModalVisible(true)}
              >
                Nieuwe Order
              </Button>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={filteredOrders}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: isMobile ? false : true,
              showTotal: (total, range) => `${range[0]}-${range[1]} van ${total} bestellingen`,
            }}
            scroll={{ x: 'max-content' }}
            size={isMobile ? 'small' : 'middle'}
          />
        </Space>
      </Card>
      
      {isDetailVisible && currentOrder && (
        <OrderDetail 
          order={currentOrder}
          onClose={() => setIsDetailVisible(false)}
          isAdmin={true}
          onRefresh={fetchOrders}
          companyName={currentOrder.klant_id ? companyNames[currentOrder.klant_id] : undefined}
        />
      )}
      
      <AdminNewOrderDialog
        open={isNewOrderModalVisible}
        onClose={() => setIsNewOrderModalVisible(false)}
        onSubmit={handleCreateOrder}
      />
    </div>
  );
} 